import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { StyledButton } from '../ui';

/**
 * UpdateCard - Reusable card component for Research, Stories, Media, and Community items
 *
 * @param {Object} props - Component props
 * @param {string} props.category - Category label (Research, Stories, Media, Community)
 * @param {string} props.title - Title of the content
 * @param {string} props.subtitle - Subtitle/description
 * @param {string} props.status - Status/action button text (Business Case, EBOOK, etc.)
 * @param {string} props.link - Link URL
 * @param {string} props.linkType - Type of link ('internal' or 'external')
 * @param {boolean} props.available - Whether the item is available
 * @param {number} props.index - Card index for alternating border pattern
 * @param {string} [props.image] - Optional image rendered flush to the top of the card
 * @param {string} [props.imageAlt] - Alt text for the optional image
 * @returns {JSX.Element} Update card component
 */
function UpdateCard({
  category,
  title,
  subtitle,
  status,
  link,
  linkType,
  available = true,
  index = 0,
  image,
  imageAlt = '',
}) {
  const navigate = useNavigate();
  const isEven = index % 2 === 0;

  const getButtonStyles = () => {
    return {
      backgroundColor: 'var(--color-primary)',
      color: 'var(--color-primary-text)',
      border: 'none',
      '&:hover': {
        backgroundColor: 'var(--color-primary-dark)',
      },
    };
  };

  return (
    <Box
      sx={{
        backgroundColor: available
          ? 'color-mix(in srgb, var(--color-primary) 8%, transparent)'
          : 'var(--color-background-light)',
        position: 'relative',
        p: 3,
        boxShadow: available ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
        transition: 'all 0.3s ease',
        borderRadius: '12px',
        border: available
          ? '1px solid var(--color-border)'
          : '1px dashed var(--color-border)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '280px',
        opacity: available ? 1 : 0.6,
        // Create partial green borders on corners - only for available items
        '&::before': available
          ? {
              content: '""',
              position: 'absolute',
              top: -1,
              left: isEven ? -1 : 'auto',
              right: !isEven ? -1 : 'auto',
              width: '50px',
              height: '50px',
              borderTop: '3px solid var(--color-primary)',
              borderLeft: isEven ? '3px solid var(--color-primary)' : 'none',
              borderRight: !isEven ? '3px solid var(--color-primary)' : 'none',
              borderRadius: 0,
            }
          : {},
        '&::after': available
          ? {
              content: '""',
              position: 'absolute',
              bottom: -1,
              right: isEven ? -1 : 'auto',
              left: !isEven ? -1 : 'auto',
              width: '50px',
              height: '50px',
              borderBottom: '3px solid var(--color-primary)',
              borderRight: isEven ? '3px solid var(--color-primary)' : 'none',
              borderLeft: !isEven ? '3px solid var(--color-primary)' : 'none',
              borderRadius: 0,
            }
          : {},
      }}
    >
      {/* Optional image header. The card carries `p: 3`, so the image is pulled
          back out to the card edges and re-rounds the two top corners to sit
          flush inside the container's 12px radius. */}
      {image && (
        <Box
          component="img"
          src={image}
          alt={imageAlt}
          sx={{
            width: 'auto',
            display: 'block',
            mx: -3,
            mt: -3,
            mb: 3,
            height: '180px',
            objectFit: 'cover',
            alignSelf: 'stretch',
            borderRadius: '12px 12px 0 0',
          }}
        />
      )}

      <Box sx={{ flex: 1 }}>
        {/* Category Label */}
        <Typography
          variant="subtitle2"
          sx={{
            color: 'var(--color-primary)',
            fontWeight: 600,
            fontSize: '0.875rem',
            mb: 1,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {category}
        </Typography>

        <Typography
          variant="h5"
          sx={{
            color: 'var(--color-heading)',
            fontWeight: 600,
            fontSize: '1.25rem',
            mb: 2,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'var(--color-text-muted)',
            fontSize: '0.9rem',
            lineHeight: 1.6,
          }}
        >
          {subtitle}
        </Typography>
      </Box>

      {/* Only show button if there's a valid link */}
      {link && link !== '#' && (
        <StyledButton
          variant="contained"
          disabled={!available}
          onClick={() => {
            if (!available) {
              return;
            }
            if (linkType === 'external') {
              window.open(link, '_blank', 'noopener,noreferrer');
            } else {
              // Force a full page reload for form pages to avoid reCAPTCHA conflicts
              const formPages = [
                '/assessment/',
                '/ebooks/',
                '/solution-briefs/',
                '/solution-guides/',
                '/business-outcome/',
                '/lets-collaborate',
                '/case-studies/rising-routes-alliance',
                '/case-studies/nonprofit-amazon-connect',
                '/updates/rochelle-manns-milestone-circles',
                '/updates/amazon-quick-phoenix-user-group',
              ];
              const needsReload = formPages.some((path) => link.includes(path));

              if (needsReload) {
                window.location.href = link;
              } else {
                navigate(link);
              }
            }
          }}
          sx={{
            mt: 3,
            alignSelf: 'flex-start',
            ...(available
              ? getButtonStyles()
              : {
                  backgroundColor: 'var(--color-muted)',
                  color: 'var(--color-text-muted)',
                  cursor: 'not-allowed',
                  '&:hover': {
                    backgroundColor: 'var(--color-muted)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'var(--color-muted)',
                    color: 'var(--color-text-muted)',
                  },
                }),
          }}
        >
          {status}
        </StyledButton>
      )}

      {/* Show status text without button if no link */}
      {(!link || link === '#') && (
        <Typography
          variant="body2"
          sx={{
            mt: 3,
            color: 'var(--color-primary)',
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {status}
        </Typography>
      )}
    </Box>
  );
}

export default UpdateCard;
